import {Box, Fab, Grid, TextField, Tooltip} from '@mui/material';
import {FC} from 'react';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import {MultimediaFileInfo} from '@/types/matrix';

interface TextMessageInputViewProps {
  text: string;
  onTextChange: (text: string) => void;
  onSendClick: () => void;
  onMultimediaUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sendTooltipLabel: string;
  inputRef: React.RefObject<HTMLInputElement>;
  multimediaFiles: MultimediaFileInfo[];
  sendImageTooltipLabel: string;
}

const TextMessageInputView: FC<TextMessageInputViewProps> = ({
  text,
  onTextChange,
  onSendClick,
  onMultimediaUpload,
  sendTooltipLabel,
  sendImageTooltipLabel,
  inputRef,
  multimediaFiles
}) => {
  const {isRoomJoined, isLoadingRoom, isSendingEvent, multimediaTypeUploaded} =
    useMatrixRoomContext();

  const isAuthorizedToSendEvents = !!(isRoomJoined && !isLoadingRoom);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isTextOrImagePresent = text.trim() !== '' || multimediaFiles !== null;

    if (event.key === 'Enter' && !event.shiftKey && isTextOrImagePresent) {
      event.preventDefault();
      onSendClick();
    }
  };

  const noMoreImagesAllowed = multimediaFiles && multimediaFiles?.length >= 10;

  // TODO: For now we only allow images. Videos should be implemented as next feature.

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 2,
        marginBottom: 2
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={10}>
          <TextField
            onKeyDown={handleKeyPress}
            inputRef={inputRef}
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            label={sendTooltipLabel}
            fullWidth
            multiline
            rows={3}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Box
            display="flex"
            justifyContent={{xs: 'center', md: 'flex-start'}}
            alignItems="center"
          >
            <Box marginRight={{xs: 2, md: 1}}>
              <input
                type="file"
                ref={inputRef}
                onChange={onMultimediaUpload}
                hidden
                //accept={'.jpg, .jpeg, .png, .gif'}
                accept={
                  multimediaTypeUploaded === 'video'
                    ? '.mp4'
                    : multimediaTypeUploaded === 'image'
                    ? '.jpg, .jpeg, .png, .gif'
                    : '.jpg, .jpeg, .png, .gif, .mp4'
                }
                multiple
                disabled={noMoreImagesAllowed ? true : undefined}
              />
              <Tooltip
                title={sendImageTooltipLabel}
                PopperProps={{
                  popperOptions: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 25]
                        }
                      }
                    ]
                  }
                }}
              >
                <span style={{cursor: 'pointer', pointerEvents: 'none'}}>
                  <Fab
                    color="primary"
                    aria-label="add"
                    size="small"
                    sx={{marginTop: 4}}
                    onClick={() => inputRef.current && inputRef.current.click()}
                    style={{pointerEvents: 'auto'}}
                    disabled={!isAuthorizedToSendEvents || isSendingEvent}
                  >
                    <ImageIcon fontSize="small" />
                  </Fab>
                </span>
              </Tooltip>
            </Box>
            <Box marginLeft={{xs: 2, md: 1}}>
              <Tooltip
                title={sendTooltipLabel}
                PopperProps={{
                  popperOptions: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 25]
                        }
                      }
                    ]
                  }
                }}
              >
                <span style={{cursor: 'pointer', pointerEvents: 'none'}}>
                  <Fab
                    color="primary"
                    aria-label="add"
                    size="small"
                    sx={{marginTop: 4}}
                    onClick={onSendClick}
                    disabled={
                      (text.trim() === '' && !multimediaFiles) ||
                      !isAuthorizedToSendEvents ||
                      isSendingEvent
                    }
                    style={{pointerEvents: 'auto'}}
                  >
                    <SendIcon fontSize="small" />
                  </Fab>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TextMessageInputView;
